import styled from '@emotion/styled';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {EUndecoratedButton} from '@ELib/components/EButtons';
import {ESmallText} from '@ELib/components/EText';
import {ETextInput} from '@ELib/components/form/ETextInput';
import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';

import {GroupWrapper} from '../sharedStyles/login';

import {abortPasswordReset, initResetWithOTP} from './resetLogic';
import {SubviewProps} from './types';

const DEFAULT_TIME_TO_OTP_EXP = 1800000; // 30 min
const RESEND_ENABLE_TIME = 7000; // 7 sec

export const OTPStep = ({
  form,
  setForm,
  setError,
}: SubviewProps): React.ReactElement => {
  const {t} = useTranslation('lib');
  const [timeLeft, setTimeLeft] = useState(DEFAULT_TIME_TO_OTP_EXP);
  const [resendDisabled, setResendDisabled] = useState(true);

  const setOTP = (otp: string) => setForm({...form, otp});

  const handleResendOTP = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      // disable resend and re-enable it after a set amount of seconds
      setResendDisabled(true);
      setTimeout(() => setResendDisabled(false), RESEND_ENABLE_TIME);
      // reset the flow and restart it
      let err = await abortPasswordReset();
      if (err) {
        setError(err);
        return;
      }
      err = await initResetWithOTP(form);
      if (err) {
        setError(err);
      } else {
        setTimeLeft(DEFAULT_TIME_TO_OTP_EXP);
      }
    },
    [form, setError]
  );

  // side effect to update the timer as long as time left is greater then 0
  // update the timer every second
  useEffect(() => {
    const timer = setTimeout(() => {
      if (timeLeft > 0) {
        const newTime = timeLeft - 1000;
        setTimeLeft(newTime > 0 ? newTime : 0);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Enable resend after a set amount of seconds
  useMountEffect(() => {
    setTimeout(() => setResendDisabled(false), RESEND_ENABLE_TIME);
  });

  return (
    <GroupWrapper>
      <ETextInput
        label={t('login.inputs.otp')}
        value={form.otp}
        onChange={(event) => setOTP(event.target.value)}
      />
      <TextWrapper>
        {t('login.passwordReset.otpCountdown', {
          countDown: formatCountdown(timeLeft),
        })}
        <br />
        {t('login.passwordReset.resendText')}{' '}
        <EUndecoratedButton
          disabled={resendDisabled}
          onClick={(e) => {
            handleResendOTP(e);
          }}
        >
          {t('login.passwordReset.resendButton')}
        </EUndecoratedButton>
      </TextWrapper>
    </GroupWrapper>
  );
};

// Format the time to <minutes>:<seconds> with the seconds padded to 2 places
const formatCountdown = (timeLeft: number) =>
  `${Math.floor((timeLeft / 1000 / 60) % 60)}:${String(
    Math.floor((timeLeft / 1000) % 60)
  ).padStart(2, '0')}`;

const TextWrapper = styled(ESmallText)`
  margin-top: var(--gutter);
`;
